import type {
  BonusShopItemDto,
  BonusShopRequestDto,
  BonusProgramSettingsDto,
  CreateBonusShopItemDto,
  CreateBonusShopRequestDto,
  TierBadgeDto,
  TransitionBonusShopRequestDto,
  UpdateBonusProgramSettingsDto,
  UpdateBonusShopItemDto,
  UpsertTiersDto,
} from '@schwrz/shared';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import { api } from '../shared/api/client';

import { useGamificationStore } from './gamification';

export const useBonusShopStore = defineStore('bonusShop', () => {
  const items = ref<BonusShopItemDto[]>([]);
  const requests = ref<BonusShopRequestDto[]>([]);
  const tiers = ref<TierBadgeDto[]>([]);
  const loading = ref(false);
  const gamificationEnabled = ref(false);
  const bonusProgramEnabled = ref(true);
  const programLoaded = ref(false);

  /** Shop + point earning UI — requires workspace gamification and bonus program. */
  const programEnabled = computed(
    () => gamificationEnabled.value && bonusProgramEnabled.value,
  );

  const pendingRequests = computed(() => requests.value.filter((r) => r.status === 'pending'));
  const decidedRequests = computed(() => requests.value.filter((r) => r.status !== 'pending'));

  function applyProgramSettings(settings: BonusProgramSettingsDto): void {
    gamificationEnabled.value = settings.gamificationEnabled;
    bonusProgramEnabled.value = settings.bonusProgramEnabled;
    programLoaded.value = true;
  }

  async function loadProgram(force = false): Promise<void> {
    if (programLoaded.value && !force) return;
    const r = await api<BonusProgramSettingsDto>('/api/bonus-shop/program');
    applyProgramSettings(r);
  }

  async function setGamificationSettings(
    patch: UpdateBonusProgramSettingsDto,
  ): Promise<void> {
    const r = await api<BonusProgramSettingsDto>('/api/bonus-shop/program', {
      method: 'PATCH',
      body: patch,
    });
    applyProgramSettings(r);
  }

  async function setProgramEnabled(enabled: boolean): Promise<void> {
    await setGamificationSettings({ bonusProgramEnabled: enabled });
  }

  async function loadTiers(): Promise<void> {
    const t = await api<{ tiers: TierBadgeDto[] }>('/api/bonus-shop/tiers');
    tiers.value = t.tiers;
  }

  async function loadAll(): Promise<void> {
    loading.value = true;
    try {
      const [c, r, t] = await Promise.all([
        api<{ items: BonusShopItemDto[] }>('/api/bonus-shop/catalog'),
        api<{ requests: BonusShopRequestDto[] }>('/api/bonus-shop/requests'),
        api<{ tiers: TierBadgeDto[] }>('/api/bonus-shop/tiers'),
      ]);
      items.value = c.items;
      requests.value = r.requests;
      tiers.value = t.tiers;
    } finally {
      loading.value = false;
    }
  }

  async function refreshRequests(): Promise<void> {
    const r = await api<{ requests: BonusShopRequestDto[] }>('/api/bonus-shop/requests');
    requests.value = r.requests;
  }

  // ---------- Catalog (admin) ----------
  async function createItem(payload: CreateBonusShopItemDto): Promise<void> {
    const row = await api<BonusShopItemDto>('/api/bonus-shop/catalog', {
      method: 'POST',
      body: payload,
    });
    items.value = [...items.value, row];
  }

  async function updateItem(id: string, payload: UpdateBonusShopItemDto): Promise<void> {
    const row = await api<BonusShopItemDto>(`/api/bonus-shop/catalog/${id}`, {
      method: 'PATCH',
      body: payload,
    });
    items.value = items.value.map((i) => (i.id === id ? row : i));
  }

  async function removeItem(id: string): Promise<void> {
    await api<void>(`/api/bonus-shop/catalog/${id}`, { method: 'DELETE' });
    items.value = items.value.filter((i) => i.id !== id);
  }

  // ---------- Tiers (admin) ----------
  async function saveTiers(payload: UpsertTiersDto): Promise<void> {
    const r = await api<{ tiers: TierBadgeDto[] }>('/api/bonus-shop/tiers', {
      method: 'PUT',
      body: payload,
    });
    tiers.value = r.tiers;
  }

  // ---------- Requests ----------
  async function buy(payload: CreateBonusShopRequestDto): Promise<void> {
    const row = await api<BonusShopRequestDto>('/api/bonus-shop/requests', {
      method: 'POST',
      body: payload,
    });
    requests.value = [row, ...requests.value];
    await useGamificationStore().refresh();
  }

  async function transition(
    id: string,
    payload: TransitionBonusShopRequestDto,
  ): Promise<void> {
    const row = await api<BonusShopRequestDto>(`/api/bonus-shop/requests/${id}`, {
      method: 'PATCH',
      body: payload,
    });
    requests.value = requests.value.map((r) => (r.id === id ? row : r));
    await useGamificationStore().refresh();
  }

  function itemById(id: string): BonusShopItemDto | undefined {
    return items.value.find((i) => i.id === id);
  }

  return {
    items,
    requests,
    tiers,
    loading,
    gamificationEnabled,
    bonusProgramEnabled,
    programEnabled,
    programLoaded,
    pendingRequests,
    decidedRequests,
    loadProgram,
    setGamificationSettings,
    setProgramEnabled,
    loadAll,
    loadTiers,
    refreshRequests,
    createItem,
    updateItem,
    removeItem,
    saveTiers,
    buy,
    transition,
    itemById,
  };
});
